<template>
  <div class="help-center">
    <div class="search-section">
      <input
        v-model="searchQuery"
        @keyup.enter="onSearch"
        class="search-box"
        type="text"
        placeholder="Search help articles..."
      />
      <button class="search-btn" @click="onSearch">
        <i class="fas fa-search"></i>
      </button>
    </div>

    <div v-if="!showDetail">
      <div v-if="loading" class="loading">Loading...</div>
      <div v-else>
        <div v-if="articles.length === 0" class="no-results">
          No articles found.
        </div>
        <div v-else>
          <div
            v-for="article in articles"
            :key="article.id"
            class="article-snippet"
          >
            <a href="#" class="article-title" @click.prevent="openDetail(article)">
              {{ article.title }}
            </a>
            <div class="snippet">{{ article.snippet }}</div>
            <div class="meta">
              <span class="date">{{ formatDate(article.updated_at) }}</span>
            </div>
          </div>
          <div class="pagination">
            <button :disabled="page === 1" @click="prevPage">Prev</button>
            <span>Page {{ page }}</span>
            <button :disabled="!hasNext" @click="nextPage">Next</button>
          </div>
        </div>
      </div>
    </div>

    <div v-else>
      <button class="back-btn" @click="closeDetail">
        <i class="fas fa-arrow-left"></i> Back to list
      </button>
      <div v-if="detailLoading" class="loading">Loading...</div>
      <div v-else-if="articleDetail">
        <h2 class="detail-title">{{ articleDetail.title }}</h2>
        <div class="detail-meta">
          <span class="date">{{ formatDate(articleDetail.updated_at) }}</span>
          <span class="read-count">Read: {{ articleDetail.read_count }}</span>
          <span v-if="articleDetail.tags && articleDetail.tags.length" class="tags">
            <span v-for="tag in articleDetail.tags" :key="tag.id" class="tag"><em>#{{ tag.name }}</em></span>
          </span>
        </div>
        <div
          class="detail-content"
          v-html="renderedContent"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { marked } from 'marked'

export default {
  name: 'HelpCenter',
  data() {
    return {
      articles: [],
      page: 1,
      pageSize: 10,
      hasNext: false,
      loading: false,
      searchQuery: '',
      showDetail: false,
      articleDetail: null,
      detailLoading: false,
      renderedContent: '',
    }
  },
  methods: {
    fetchArticles(page = 1) {
      this.loading = true
      let url = `${process.env.VUE_APP_API_BASE_URL}/api/knowledgebase/articles/?ordering=-updated_at&page=${page}&page_size=${this.pageSize}`
      axios
        .get(url)
        .then((res) => {
          this.articles = res.data.results
          this.hasNext = !!res.data.next
          this.page = page
        })
        .catch(() => {
          this.articles = []
          this.hasNext = false
        })
        .finally(() => {
          this.loading = false
        })
    },
    searchArticles(page = 1) {
      if (!this.searchQuery) {
        this.fetchArticles(page)
        return
      }
      this.loading = true
      let url = `${process.env.VUE_APP_API_BASE_URL}/api/knowledgebase/articles/search/?q=${encodeURIComponent(this.searchQuery)}&page=${page}&page_size=${this.pageSize}`
      axios
        .get(url)
        .then((res) => {
          this.articles = res.data.results
          this.hasNext = !!res.data.next
          this.page = page
        })
        .catch(() => {
          this.articles = []
          this.hasNext = false
        })
        .finally(() => {
          this.loading = false
        })
    },
    onSearch() {
      this.page = 1
      this.searchArticles(1)
    },
    nextPage() {
      const nextPage = this.page + 1
      if (this.searchQuery) {
        this.searchArticles(nextPage)
      } else {
        this.fetchArticles(nextPage)
      }
    },
    prevPage() {
      if (this.page === 1) return
      const prevPage = this.page - 1
      if (this.searchQuery) {
        this.searchArticles(prevPage)
      } else {
        this.fetchArticles(prevPage)
      }
    },
    openDetail(article) {
      this.showDetail = true
      this.detailLoading = true
      this.articleDetail = null
      this.renderedContent = ''
      axios
        .get(`${process.env.VUE_APP_API_BASE_URL}/api/knowledgebase/articles/${article.id}/`)
        .then((res) => {
          this.articleDetail = res.data
          this.renderedContent = marked(res.data.content || '')
          // Increment read count
          axios.post(`${process.env.VUE_APP_API_BASE_URL}/api/knowledgebase/articles/${article.id}/increment_read_count/`).catch(() => {})
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    closeDetail() {
      this.showDetail = false
      this.articleDetail = null
      this.renderedContent = ''
    },
    formatDate(dateStr) {
      const d = new Date(dateStr)
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
  mounted() {
    this.fetchArticles()
  },
}
</script>

<style scoped>
.help-center {
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
  font-family: 'Inter', Arial, sans-serif;
}

.search-section {
  margin-bottom: 2.5rem;
}

.search-container {
  display: flex;
  max-width: 600px;
  margin: 0 auto;
  position: relative;
}

.search-box {
  flex: 1;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px 0 0 8px;
  outline: none;
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.search-box:focus {
  border-color: #4a89dc;
  box-shadow: 0 0 0 3px rgba(74, 137, 220, 0.1);
}

.search-btn {
  padding: 0 1.5rem;
  background: #4a89dc;
  color: white;
  border: none;
  border-radius: 0 8px 8px 0;
  cursor: pointer;
  transition: background 0.3s ease;
}

.search-btn:hover {
  background: #3a70c2;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4a89dc;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  color: #666;
  font-size: 1rem;
}

.article-list {
  margin-top: 1.5rem;
}

.article-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 1rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}

.article-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.card-content {
  padding: 1.5rem;
}

.article-title {
  font-size: 1.2rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.article-snippet {
  color: #666;
  margin-bottom: 0.75rem;
  line-height: 1.5;
}

.meta {
  display: flex;
  align-items: center;
  color: #888;
  font-size: 0.9rem;
}

.meta i {
  margin-right: 0.5rem;
}

.no-results {
  text-align: center;
  padding: 3rem;
  color: #888;
}

.no-results i {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #ccc;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2rem 0;
  gap: 1rem;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #e9ecef;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-number {
  font-size: 0.9rem;
  color: #666;
}

.back-btn {
  background: none;
  border: none;
  color: #4a89dc;
  font-size: 0.9rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0.5rem 0;
}

.back-btn:hover {
  text-decoration: underline;
}

.detail-container {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.detail-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
}

.detail-title {
  font-size: 1.8rem;
  color: #2c3e50;
  margin-bottom: 1rem;
}

.detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #666;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tags {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tag {
  background: #e9f2ff;
  color: #4a89dc;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
}

.detail-content {
  line-height: 1.7;
  color: #333;
}

.detail-content >>> h2 {
  font-size: 1.4rem;
  margin: 1.5rem 0 1rem;
  color: #2c3e50;
}

.detail-content >>> p {
  margin-bottom: 1rem;
}

.detail-content >>> a {
  color: #4a89dc;
  text-decoration: none;
}

.detail-content >>> a:hover {
  text-decoration: underline;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>